import React, { useState, useEffect, useMemo } from 'react';
import { camelCase } from 'lodash-es';
import { logAction } from '../../../utils/scoringLog';
import { useLocation, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { BackIcon } from '../../../components/AppHeader';
import PeopleIcon from '@mui/icons-material/People';
import RemoveIcon from '@mui/icons-material/Remove';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { useTranslation } from 'react-i18next';
import ScoreForm from '../../../components/ScoreForm';
import Snackbar from '../../../components/Snackbar';
import { getFlagSrc, getLatestScoreForShooterPerStage } from '../../../utils/scoring';

const DQ_CATEGORIES = ['10.4', '10.5', '10.6', '10.7'];

const DQ_REASONS = {
  '10.4': ['10-4-1', '10-4-2', '10-4-3', '10-4-4', '10-4-5', '10-4-6', '10-4-7'],
  '10.5': ['10-5-1', '10-5-2', '10-5-3', '10-5-4', '10-5-5', '10-5-6', '10-5-7',
    '10-5-8', '10-5-9', '10-5-10', '10-5-11', '10-5-12', '10-5-13', '10-5-14', '10-5-15', '10-5-16'],
  '10.6': ['10-6-1', '10-6-2'],
  '10.7': ['10-7-0'],
};

const PENALTY_RULES_POPULAR = ['10-2-1', '9-5-6-7', '2-2-1-5', '8-7-1'];
const PENALTY_RULES_MORE = ['10-2-2', '1-1-5-2', '10-2-5', '4-5-1', '10-2-6', '10-2-8', '10-2-9', '10-2-11', 'general'];
const PENALTY_RULES_OTHERS = ['8-6-2', '8-7-2', '9-1-1'];

const emptyScore = () => ({
  scorePerTarget: [],
  poppers: 0,
  tenPointsPoppers: 0,
  'no-shoots': 0,
  penaltiesByType: {},
  specialPenalty: 0,
  time: 0,
  bonusField: 0,
});

export default function ScoreEntry() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { stageIdx, squadId, shooterId } = useParams();
  const { matchId, match, stages, shooters, scores, setPendingScore } = useOutletContext();
  const stageIdxNum = parseInt(stageIdx, 10);
  const basePath = `/${matchId}/manage/scores`;
  const scorePath = `${basePath}/stage/${stageIdx}/squad/${squadId}/score-mode`;
  const [searchParams] = useSearchParams();
  const fromSearch = searchParams.get('fromSearch') || squadId === '_nosquad';
  const splitParam = searchParams.get('split');
  const splitQs = splitParam ? `?split=${splitParam}` : '';
  const backPath = fromSearch ? `${basePath}/stage/${stageIdx}` : `${scorePath}${splitQs}`;

  const stage = stages[stageIdxNum - 1];
  const shooter = shooters.find((s) => s.id === shooterId);
  const scoreMode = match?.scoreMode || 'comstock';

  // Persist scoring form in localStorage so it survives refresh
  const storageKey = `__scoring_entry_${matchId}_${stageIdx}`;
  const saved = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(storageKey)); } catch { return null; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // If coming back from signature review, restore from cache directly (no prompt).
  // If fresh open with cached data, show a prompt.
  const fromSignature = location.state?.fromSignature;
  const initData = useMemo(() => {
    if (fromSignature && saved && (!saved.shooterId || saved.shooterId === shooterId)) {
      return {
        scoreData: saved.scoreData || { ...emptyScore(), powerFactor: shooter?.['power-factor'] || 'minor' },
        isDq: saved.isDq || false,
        dqReason: saved.dqReason || '',
        dqOccuredOn: saved.dqOccuredOn || null,
      };
    }
    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultNow = () => {
    const now = new Date();
    const pad2 = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}T${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
  };

  const [showLoadPrompt, setShowLoadPrompt] = useState(() => !fromSignature && !!saved?.scoreData && saved?.shooterId === shooterId);
  const [scoreData, setScoreData] = useState(
    initData?.scoreData || { ...emptyScore(), powerFactor: shooter?.['power-factor'] || 'minor' },
  );
  const [isDq, setIsDq] = useState(initData?.isDq || false);
  const [dqReason, setDqReason] = useState(initData?.dqReason || '');
  const [dqOccuredOn, setDqOccuredOn] = useState(initData?.dqOccuredOn || defaultNow());
  const [dqCategory, setDqCategory] = useState(null);
  const [showDq, setShowDq] = useState(false);
  const [missConfirm, setMissConfirm] = useState(null);
  const [showPenalties, setShowPenalties] = useState(false);
  const [showMorePenalties, setShowMorePenalties] = useState(false);
  const [nextShooters, setNextShooters] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [muted, setMuted] = useState(() => localStorage.getItem('__scoring_mute') === '1');

  const loadFromCache = () => {
    if (saved) {
      if (saved.scoreData) setScoreData(saved.scoreData);
      if (saved.isDq) setIsDq(saved.isDq);
      if (saved.dqReason) setDqReason(saved.dqReason);
      if (saved.dqOccuredOn) setDqOccuredOn(saved.dqOccuredOn);
    }
    setShowLoadPrompt(false);
  };

  // Save form state to localStorage on every change (but not while prompt is showing)
  useEffect(() => {
    if (!showLoadPrompt) {
      localStorage.setItem(storageKey, JSON.stringify({ scoreData, isDq, dqReason, dqOccuredOn, shooterId }));
    }
  }, [storageKey, scoreData, isDq, dqReason, dqOccuredOn, showLoadPrompt]);

  const totalPenalties = Object.values(scoreData.penaltiesByType || {}).reduce((s, v) => s + v, 0);

  const handleShowNextShooters = () => {
    const latestScores = getLatestScoreForShooterPerStage({ scores, numStages: stages.length, stages });
    // Get squad shooters who are not absent and not DQ'd
    let sqShooters = shooters.filter((s) => s.squad === squadId
      && ((s.equipmentChecks || [])[stageIdxNum] || {}).check !== 'absent');
    latestScores.forEach((stageScores, stIdx) => {
      stageScores.forEach((s) => {
        if (s.dq || stIdx === stageIdxNum - 1) {
          sqShooters = sqShooters.filter((sh) => sh.id !== s.shooterId);
        }
      });
    });
    const index = sqShooters.findIndex((s) => s.id === shooterId);
    sqShooters = [...sqShooters.slice(index), ...sqShooters.slice(0, index)];
    sqShooters = sqShooters.filter((s) => s.id !== shooterId);
    setNextShooters(sqShooters.slice(0, 3));
  };

  const countMisses = () => {
    if (isDq) return 0;
    const spt = scoreData.scorePerTarget || [];
    let misses = 0;
    spt.forEach((s) => { misses += (s || '').split('').filter((c) => c === 'M').length; });
    if (scoreMode !== 'steel-no-miss' && stage) {
      const targets = stage.targets || {};
      const numMetal = (targets.poppers || 0) + (targets.minipoppers || 0) + (targets.plates || 0) + (targets['pcc-frangible'] || 0);
      const numTenPt = (targets['pcc-ten-points-poppers'] || 0) + (targets['pcc-ten-points-frangible'] || 0);
      misses += Math.max(0, numMetal - (scoreData.poppers || 0));
      misses += Math.max(0, numTenPt - (scoreData.tenPointsPoppers || 0));
    }
    return misses;
  };

  const proceedToSignature = () => {
    // Store score data in context and navigate to signature page
    if (isDq) {
      setPendingScore({
        stageIdx: stageIdxNum,
        shooterId,
        dq: dqReason,
        occuredOn: dqOccuredOn ? new Date(dqOccuredOn).getTime() : stageIdxNum,
        isDq: true,
      });
    } else {
      setPendingScore({
        stageIdx: stageIdxNum,
        shooterId,
        scorePerTarget: scoreData.scorePerTarget || [],
        poppers: scoreData.poppers || 0,
        tenPointsPoppers: scoreData.tenPointsPoppers || 0,
        'no-shoots': scoreData['no-shoots'] || 0,
        penaltiesByType: scoreData.penaltiesByType || {},
        specialPenalty: scoreData.specialPenalty || 0,
        bonusField: scoreData.bonusField || 0,
        time: scoreData.time || 0,
        isDq: false,
      });
    }
    logAction('SCORE_END', { matchId, stageIdx: stageIdxNum, shooterId }, isDq ? { isDq: true, dqReason } : { scorePerTarget: scoreData.scorePerTarget, poppers: scoreData.poppers, time: scoreData.time, penalties: scoreData.penaltiesByType });
    const sigQs = fromSearch ? '?fromSearch=1' : splitQs;
    navigate(`${scorePath}/scoring/${shooterId}/signature${sigQs}`);
  };

  const handleEndScore = () => {
    if (!isDq && !scoreData.time) {
      setSnack({ open: true, message: t('scoring.missingTime'), severity: 'error' });
      return;
    }

    if (!isDq && stage) {
      const targets = stage.targets || {};
      const numPaper = (targets.paper || 0) + (targets.minipaper || 0);
      const numMetal = (targets.poppers || 0) + (targets.minipoppers || 0) + (targets.plates || 0) + (targets['pcc-frangible'] || 0);
      const numTenPt = (targets['pcc-ten-points-poppers'] || 0) + (targets['pcc-ten-points-frangible'] || 0);
      const shotsPerTarget = numPaper > 0 ? (stage.rounds - numMetal - numTenPt) / numPaper : 0;
      const spt = scoreData.scorePerTarget || [];
      if (numPaper > 0) {
        const unscored = Array.from({ length: numPaper }).filter((_, i) => (spt[i] || '').length < shotsPerTarget);
        if (unscored.length > 0) {
          setSnack({ open: true, message: t('scoring.unscoredTargets', { count: unscored.length }), severity: 'error' });
          return;
        }
      }
    }

    const misses = countMisses();
    if (misses > 0) {
      setMissConfirm(misses);
      return;
    }

    proceedToSignature();
  };

  if (!shooter || !stage) {
    return <Typography color="error">Shooter or stage not found</Typography>;
  }

  const parts = (shooter.publicId || '').split('|');
  const displayId = parts[parts.length - 1];
  const countryCode = parts.length > 1 ? parts[0] : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)}>
          {t('common.back')}
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-evenly', flex: 1 }}>
          <Button color={muted ? 'error' : 'primary'} size="large" sx={{ minWidth: 0 }}
            onClick={() => { const next = !muted; setMuted(next); localStorage.setItem('__scoring_mute', next ? '1' : '0'); }}>
            {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
          </Button>
          <Button color="primary" size="large" onClick={handleShowNextShooters} sx={{ minWidth: 0 }}>
            <PeopleIcon />
          </Button>
          <IconButton color="warning" onClick={() => { setScoreData({ ...emptyScore(), powerFactor: shooter?.['power-factor'] || 'minor' }); setIsDq(false); setDqReason(''); }}>
            <DeleteSweepIcon />
          </IconButton>
          <Button variant="outlined" color="error" onClick={() => { setDqCategory(null); setShowDq(true); }}
            sx={{ minWidth: 0, fontWeight: 700 }}>
            DQ
          </Button>
        </Box>
      </Box>

      {/* Shooter info banner */}
      <Box sx={{ textAlign: 'center', mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">{t('results.stageN', { n: stageIdx })}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mb: 0.25 }}>
          {countryCode && (
            <img alt={countryCode} src={getFlagSrc({ countryCode, size: 20 })} style={{ display: 'block' }} />
          )}
          <Typography variant="body2" color="text.secondary">#{displayId}</Typography>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{shooter.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {[
            shooter.division ? t(`divisions.${camelCase(shooter.division)}`, shooter.division) : null,
            shooter.category ? t(`categories.${camelCase(shooter.category)}`, shooter.category) : null,
            shooter['power-factor'] ? t(`registration.${camelCase(shooter['power-factor'])}`, shooter['power-factor']) : null,
          ].filter(Boolean).join(' | ')}
        </Typography>
      </Box>

      {isDq ? (
        <Box sx={{ p: 2, border: '2px solid', borderColor: 'error.main', borderRadius: 1, mb: 2, textAlign: 'center' }}>
          <Typography variant="h5" color="error" sx={{ fontWeight: 700 }} gutterBottom>DQ</Typography>
          <Typography variant="body1">{t(`dq.${dqReason}`, dqReason)}</Typography>
          <Button size="small" onClick={() => { setIsDq(false); setDqReason(''); }} sx={{ mt: 1 }}>
            {t('common.cancel')}
          </Button>
        </Box>
      ) : (
        <ScoreForm stage={stage} scoreData={scoreData} onChange={setScoreData} scoreMode={scoreMode} />
      )}

      <Divider sx={{ my: 2 }} />

      {/* Penalties button */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Button
          variant="contained" color="secondary"
          onClick={() => setShowPenalties(true)}
          sx={{ height: 60, px: 8, width: 240 }}
        >
          {t('results.penalties')} ({totalPenalties})
        </Button>
        {scoreData.specialPenalty > 0 && (
          <Typography variant="body2">
            + {t('scoring.specialPenalty', { specialPenalty: scoreData.specialPenalty })}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* End Score button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Button variant="contained" size="large" onClick={handleEndScore} sx={{ px: 8, py: 1.5 }}>
          {t('scoring.endScore')}
        </Button>
      </Box>

      {/* DQ Dialog */}
      <Dialog open={showDq} onClose={() => setShowDq(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('scoring.pickReason')}</DialogTitle>
        <DialogContent sx={{ direction: 'ltr' }}>
          {!dqCategory ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center', py: 2 }}>
              {DQ_CATEGORIES.map((cat) => (
                <Button key={cat} variant="contained" onClick={() => setDqCategory(cat)}
                  sx={{ width: 332, height: 60, fontSize: '1.1rem' }}>
                  {t(`dq.${cat.replace(/\./g, '-')}`, cat)}
                </Button>
              ))}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center', py: 2 }}>
              {(DQ_REASONS[dqCategory] || []).map((reason) => (
                <Button key={reason} variant="contained" onClick={() => { setIsDq(true); setDqReason(reason); setShowDq(false); }}
                  sx={{ width: 332, minHeight: 48, fontSize: '0.95rem', textTransform: 'none' }}>
                  {t(`dq.${reason}`, reason)}
                </Button>
              ))}
            </Box>
          )}
          <TextField
            label={t('scoring.dqTime')} type="datetime-local" value={dqOccuredOn}
            onChange={(e) => setDqOccuredOn(e.target.value)}
            fullWidth sx={{ mt: 2 }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </DialogContent>
        <DialogActions>
          {dqCategory && (
            <Button onClick={() => setDqCategory(null)}>{t('common.back')}</Button>
          )}
          <Button onClick={() => setShowDq(false)}>{t('common.cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* Penalties Dialog */}
      <Dialog open={showPenalties} onClose={() => setShowPenalties(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>{t('scoring.penaltiesCof')}</Typography>
            {[...PENALTY_RULES_POPULAR, ...(showMorePenalties ? PENALTY_RULES_MORE : [])].map((rule) => (
              <PenaltyRow key={rule} rule={rule} t={t}
                value={(scoreData.penaltiesByType || {})[rule] || 0}
                onChange={(v) => {
                  const p = { ...(scoreData.penaltiesByType || {}) };
                  if (v === 0) delete p[rule]; else p[rule] = v;
                  setScoreData({ ...scoreData, penaltiesByType: p });
                }}
              />
            ))}
            {!showMorePenalties && (
              <Button size="small" onClick={() => setShowMorePenalties(true)} sx={{ textTransform: 'none', my: 1 }}>
                {t('scoring.penaltiesShowMore')}
              </Button>
            )}
            {showMorePenalties && (
              <>
                <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>{t('scoring.penaltiesOthers')}</Typography>
                {PENALTY_RULES_OTHERS.map((rule) => (
                  <PenaltyRow key={rule} rule={rule} t={t}
                    value={(scoreData.penaltiesByType || {})[rule] || 0}
                    onChange={(v) => {
                      const p = { ...(scoreData.penaltiesByType || {}) };
                      if (v === 0) delete p[rule]; else p[rule] = v;
                      setScoreData({ ...scoreData, penaltiesByType: p });
                    }}
                  />
                ))}
                <PenaltyRow rule="10-2-10" t={t} unit="%"
                  value={scoreData.specialPenalty || 0}
                  onChange={(v) => setScoreData({ ...scoreData, specialPenalty: v })}
                  max={20}
                />
              </>
            )}
            <Button fullWidth variant="contained" onClick={() => setShowPenalties(false)} sx={{ mt: 3 }}>
              {t('common.save')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Next shooters snackbar */}
      <Snackbar
        open={nextShooters !== null}
        severity="info"
        onClose={() => setNextShooters(null)}
        message={
          nextShooters && nextShooters.length === 0
            ? t('scoring.noNextShooters')
            : (
              <>
                <Typography variant="h6" sx={{ color: 'inherit' }}>{t('scoring.nextShooters')}</Typography>
                {(nextShooters || []).map((s) => (
                  <Typography key={s.id} variant="body2" sx={{ color: 'inherit' }}>{s.name}</Typography>
                ))}
              </>
            )
        }
      />

      {/* Load cached data prompt */}
      <Dialog open={showLoadPrompt} onClose={() => setShowLoadPrompt(false)}>
        <DialogTitle>{t('scoring.shouldLoadLocal')}</DialogTitle>
        <DialogActions>
          <Button onClick={() => setShowLoadPrompt(false)}>{t('scoring.loadNothing')}</Button>
          <Button variant="contained" onClick={loadFromCache}>{t('scoring.loadLocal')}</Button>
        </DialogActions>
      </Dialog>

      {/* Miss confirmation dialog */}
      <Dialog open={!!missConfirm} onClose={() => setMissConfirm(null)}>
        <DialogTitle>{t('scoring.missConfirmTitle', 'Misses Detected')}</DialogTitle>
        <DialogContent>
          <Typography>{t('scoring.missConfirmMsg', { count: missConfirm || 0 })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMissConfirm(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" color="warning" onClick={() => { setMissConfirm(null); proceedToSignature(); }}>
            {t('scoring.missConfirmOk', 'Yes, continue')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} message={snack.message} severity={snack.severity}
        onClose={() => setSnack({ ...snack, open: false })} />
    </Box>
  );
}

function PenaltyRow({ rule, t, value, onChange, unit = '', max = 32 }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1.5, direction: 'ltr' }}>
      <Typography variant="caption" sx={{ textAlign: 'center', mb: 0.5 }}>
        {t(`penalties.${rule}`, rule)}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Button
          variant="contained" color="primary"
          disabled={value >= max}
          onClick={() => onChange(Math.min(value + 1, max))}
          sx={{ minWidth: 40, width: 60, height: 60, p: 0 }}
        >
          <AddIcon />
        </Button>
        <Typography sx={{ fontSize: '2em', width: 80, textAlign: 'center', fontWeight: 700 }}>
          {value}{unit}
        </Typography>
        <Button
          variant="contained" color="secondary"
          disabled={value <= 0}
          onClick={() => onChange(Math.max(value - 1, 0))}
          sx={{ minWidth: 40, width: 60, height: 60, p: 0 }}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
}
